import React, {
  forwardRef,
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import OutsideClickHandler from "react-outside-click-handler";
import SpeechRecognition from "react-speech-recognition";
import TextareaAutosize from "react-textarea-autosize";
import Picker from "emoji-picker-react";
import apiService from "../Services/APIService";
import closeimg from "../../images/close-window.png";
import fileimg from "../../images/file.png";
import { requestNotification } from "../Services/HWIframeBridge";
import { s360ConfigService } from "../Services/S360Config";
import TextToSpeech from "./TextToSpeech";
import VoiceCapture from "./VoiceCapture";
import defaultBrandIcon from "../../utils/Icons/DefaultBrandIcon";
import DOMPurify from "dompurify";

import "./ConversationStyleSheet.css";
import flowMemorySingleton from "../Services/FlowMemorySingleton";

const FilePreview = React.memo(({ files, onRemovePreviewFile, CloseIconPreviewSvg }) => {
  const fileURLMap = useMemo(() => {
    const map = new Map();
    files.forEach((file) => {
      map.set(file, URL.createObjectURL(file));
    });
    return map;
  }, [files]);

  useEffect(() => {
    return () => {
      fileURLMap.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [fileURLMap]);

  return (
    <div className="file-preview-new">
      {files.map((file) => {
        const fileURL = fileURLMap.get(file);
        const isAudioFile = file.type.toLowerCase().indexOf("audio/") !== -1;
        const isVideoFile = file.type.toLowerCase().indexOf("video/") !== -1;
        const isImageFile =
          file.type.toLowerCase().indexOf("image/") !== -1 &&
          file.type.toLowerCase() !== "image/tiff";
        const isOtherFile = file.type.toLowerCase() === "";

        if (isAudioFile) {
          return (
            <div key={fileURL} className="oneAttachFile">
              <div
                className="s360-preview-close"
                onClick={() => onRemovePreviewFile && onRemovePreviewFile(file)}
              >
                {CloseIconPreviewSvg()}
              </div>
              <figure
                style={{
                  minWidth: "130px",
                  width: "100%",
                  height: "100px",
                  display: "flex",
                }}
              >
                <audio
                  style={{
                    minWidth: "130px",
                    width: "100%",
                    alignSelf: "center",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    background: "none",
                    borderRadius: "7px",
                  }}
                  controls
                  src={fileURL}
                >
                  {" "}
                  Your browser does not support the <code>audio</code> element.
                </audio>
              </figure>
            </div>
          );
        } else if (isVideoFile) {
          return (
            <div key={fileURL} className="oneAttachFile">
              <div
                className="s360-preview-close"
                onClick={() => onRemovePreviewFile(file)}
              >
                {CloseIconPreviewSvg()}
              </div>
              <video
                controls
                height="100px"
                style={{
                  height: "74px",
                  borderRadius: "7px",
                }}
              >
                <source src={fileURL} type="video/mp4" />
                Sorry, your browser doesn't support embedded videos.
              </video>
            </div>
          );
        } else if (isImageFile) {
          return (
            <div key={fileURL} className="oneAttachFile">
              <div
                className="s360-preview-close"
                onClick={() => onRemovePreviewFile(file)}
              >
                {CloseIconPreviewSvg()}
              </div>
              <img
                style={{
                  borderRadius: "7px",
                  maxHeight: "100px",
                  maxWidth: "150px",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                }}
                src={fileURL}
                alt=""
              />
            </div>
          );
        } else if (isOtherFile) {
          return (
            <div key={fileURL} className="oneAttachFile">
              <div
                className="s360-preview-close"
                onClick={() => onRemovePreviewFile(file)}
              >
                {CloseIconPreviewSvg()}
              </div>
              <a target="_blank" href={fileURL} rel="noreferrer" download>
                <img
                  width="70"
                  height="70"
                  title={fileURL}
                  src={fileimg}
                  alt="close"
                />
              </a>
            </div>
          );
        } else {
          return (
            <div key={fileURL} className="oneAttachFile">
              <div
                className="s360-preview-close"
                onClick={() => onRemovePreviewFile(file)}
              >
                {CloseIconPreviewSvg()}
              </div>
              <a target="_blank" href={fileURL} rel="noreferrer" download>
                <img
                  width="70"
                  height="70"
                  title={fileURL}
                  src={fileimg}
                  alt="close"
                />
              </a>
            </div>
          );
        }
      })}
    </div>
  );
});

const InputBox = forwardRef((props, ref) => {
  const microphoneRef = useRef();
  const [textValue, setTextValue] = useState(
    localStorage.getItem("finalvalue") ?? "",
  );
  const [sent, setSent] = useState(true);
  const [showEmoji, setShowEmoji] = useState(false);
  const isFullScreen = localStorage.getItem("openInFullScreen");
  const { disableInput, attchmentProps, fileParams, inputSpecs } = props;
  const { attachRef, onClickAttachment, onChangeAttachment } = attchmentProps;
  let attachemntFiles = useMemo(() => {
    return props.files || [];
  }, [props.files]);
  const defaultMessage = s360ConfigService.config.sandBox
    ? "Write your message"
    : "Type a message...";
  const fileSize = fileParams?.sizeLimit || null;
  const fileSizeInMB =
    (fileSize && (parseInt(fileSize) / 1024000 ** 2).toFixed(2)) || null;
  const suppourtedFiles = fileParams?.fileType;

  useEffect(() => {
    const lastShownAutoAttachment =
      flowMemorySingleton.getLastShownMessageIdForAutoAttachment();
    if (
      props.enableAttachment &&
      !s360ConfigService?.config?.hideAttachment &&
      lastShownAutoAttachment === props.enableAttachment
    ) {
      if (s360ConfigService.config?.sandBox) return;
      onClickAttachment();

      flowMemorySingleton.setLastShownMessageIdForAutoAttachment(false);
    }
  }, [props.enableAttachment, onClickAttachment]);

  const handleClick = () => {
    if (showVoiceMic && audioRecordedFile) {
      let formData = new FormData();

      const blob = new Blob([audioRecordedFile], {
        type: "audio/webm; codecs=opus",
      });
      const file = new File([blob], "AudioMessage.webm");

      let properties = {
        fromId: s360ConfigService.thrioConfig.formDetails.Email,
        toId: s360ConfigService.config.workitemId,
        type: "CLIENT",
        textMsg: textValue || "",
      };

      formData.append("object", JSON.stringify(properties));
      formData.append("file", file);
      apiService.sendMessage(formData).then((messageSentRes) => {
        setShowVoiceMic(false);
        setTextValue("");
        props.updateAudioFileinChat(file);
        setAudioRecordedFile(null);
      });
      return;
    }

    setShowEmoji(false);
    if (!s360ConfigService?.config?.sandBox) {
      if (s360ConfigService?.config?.preview) {
        if (attachemntFiles.length > 0) {
          props.send();
        } else {
          // Sanitize the text value before sending
          props.sendMessageToPreview(DOMPurify.sanitize(textValue), "");
        }

        // props.send();
      } else {
        // Sanitize the text value before sending
        props.send(DOMPurify.sanitize(textValue));
      }

      setTextValue("");
      setSent(!sent);
      localStorage.removeItem("finalvalue");
    } else if (s360ConfigService.thrioConfig) {
      const fd = {
        fromId: s360ConfigService.thrioConfig.formDetails.Email,
        toId: props.currentWorkitemId,
        type: "CLIENT",
        // Sanitize the text value before sending
        textMsg: DOMPurify.sanitize(textValue),
      };
      apiService.sendMessage(fd).then((messageSentRes) => {});
    }
  };

  const containerStyle = () => {
    return {
      width: s360ConfigService?.config?.sandBox ? "95%" : "95%",
      borderColor: "#ccc",
      color: "black",
      cursor: props.disableInput ? "not-allowed" : "text",
      ...s360ConfigService.config?.customDesign?.inputBoxInputContainer,
    };
  };

  const onTextFieldChange = (e) => {
    // Sanitize the input value on change
    const sanitizedValue = DOMPurify.sanitize(e.target.value);
    setTextValue(sanitizedValue);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      setShowEmoji(false);
      event.preventDefault();
      if (s360ConfigService?.config?.preview) {
        // Sanitize the text value before sending
        props.sendMessageToPreview(DOMPurify.sanitize(textValue), "");
      } else {
        // Sanitize the text value before sending
        props.send(DOMPurify.sanitize(textValue));
      }

      setTextValue("");
      setSent(!sent);
    } else {
      setTyping();
    }
  };

  const emojiImage = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 16 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.5 9C14.5 6.6875 13.25 4.5625 11.25 3.375C9.21875 2.21875 6.75 2.21875 4.75 3.375C2.71875 4.5625 1.5 6.6875 1.5 9C1.5 11.3438 2.71875 13.4688 4.75 14.6562C6.75 15.8125 9.21875 15.8125 11.25 14.6562C13.25 13.4688 14.5 11.3438 14.5 9ZM0 9C0 6.15625 1.5 3.53125 4 2.09375C6.46875 0.65625 9.5 0.65625 12 2.09375C14.4688 3.53125 16 6.15625 16 9C16 11.875 14.4688 14.5 12 15.9375C9.5 17.375 6.46875 17.375 4 15.9375C1.5 14.5 0 11.875 0 9ZM5.53125 10.9688C6 11.4688 6.8125 12 8 12C9.15625 12 9.96875 11.4688 10.4375 10.9688C10.7188 10.6562 11.1875 10.625 11.5 10.9062C11.8125 11.1875 11.8125 11.6562 11.5312 11.9688C10.8438 12.7188 9.65625 13.5 8 13.5C6.3125 13.5 5.125 12.7188 4.4375 11.9688C4.15625 11.6562 4.1875 11.1875 4.46875 10.9062C4.78125 10.625 5.25 10.6562 5.53125 10.9688ZM4.5 7.5C4.5 6.96875 4.9375 6.5 5.5 6.5C6.0625 6.5 6.5 6.96875 6.5 7.5C6.5 8.0625 6.0625 8.5 5.5 8.5C4.9375 8.5 4.5 8.0625 4.5 7.5ZM10.5 6.5C11.0625 6.5 11.5 6.96875 11.5 7.5C11.5 8.0625 11.0625 8.5 10.5 8.5C9.9375 8.5 9.5 8.0625 9.5 7.5C9.5 6.96875 9.9375 6.5 10.5 6.5Z"
        fill="#758BA3"
      />
    </svg>
  );

  const attachmentImage = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 13 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.6289 2.74609C9.91797 2.03516 8.74219 2.03516 8.03125 2.74609L2.80859 7.99609C1.60547 9.19922 1.60547 11.1406 2.80859 12.3164C3.98438 13.5195 5.92578 13.5195 7.12891 12.3164L11.2852 8.16016C11.5312 7.91406 11.9414 7.91406 12.1875 8.16016C12.4609 8.43359 12.4609 8.84375 12.1875 9.08984L8.05859 13.2461C6.33594 14.9688 3.57422 14.9688 1.87891 13.2461C0.15625 11.5508 0.15625 8.78906 1.87891 7.06641L7.12891 1.81641C8.33203 0.613281 10.3281 0.613281 11.5586 1.81641C12.7617 3.04688 12.7617 5.04297 11.5586 6.24609L6.52734 11.2773C5.65234 12.1523 4.23047 12.0703 3.46484 11.1133C2.80859 10.293 2.89062 9.11719 3.62891 8.37891L7.78516 4.22266C8.03125 3.97656 8.44141 3.97656 8.6875 4.22266C8.96094 4.49609 8.96094 4.90625 8.6875 5.15234L4.55859 9.30859C4.28516 9.58203 4.25781 9.99219 4.50391 10.293C4.77734 10.6211 5.26953 10.6758 5.59766 10.3477L10.6289 5.31641C11.3398 4.60547 11.3398 3.45703 10.6289 2.74609Z"
        fill="#758BA3"
      />
    </svg>
  );

  const CloseIconPreviewSvg = () => {
    return (
      <svg
        width="15"
        height="15"
        viewBox="0 0 18 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          backgroundColor: "white",
          borderRadius: "100%",
          height: "25px",
          width: "25px",
        }}
      >
        <path
          d="M9 1.5C6.65625 1.5 4.53125 2.75 3.34375 4.75C2.1875 6.78125 2.1875 9.25 3.34375 11.25C4.53125 13.2812 6.65625 14.5 9 14.5C11.3125 14.5 13.4375 13.2812 14.625 11.25C15.7812 9.25 15.7812 6.78125 14.625 4.75C13.4375 2.75 11.3125 1.5 9 1.5ZM9 16C6.125 16 3.5 14.5 2.0625 12C0.625 9.53125 0.625 6.5 2.0625 4C3.5 1.53125 6.125 0 9 0C11.8438 0 14.4688 1.53125 15.9062 4C17.3438 6.5 17.3438 9.53125 15.9062 12C14.4688 14.5 11.8438 16 9 16ZM6.46875 5.46875C6.75 5.1875 7.21875 5.1875 7.5 5.46875L8.96875 6.9375L10.4375 5.46875C10.75 5.1875 11.2188 5.1875 11.5 5.46875C11.8125 5.78125 11.8125 6.25 11.5 6.53125L10.0312 8L11.5 9.46875C11.8125 9.78125 11.8125 10.25 11.5 10.5312C11.2188 10.8438 10.75 10.8438 10.4375 10.5312L8.96875 9.0625L7.5 10.5312C7.21875 10.8438 6.75 10.8438 6.46875 10.5312C6.15625 10.25 6.15625 9.78125 6.46875 9.46875L7.9375 8L6.46875 6.53125C6.15625 6.25 6.15625 5.78125 6.46875 5.46875Z"
          fill="#7A7D8C"
        />
      </svg>
    );
  };
  const sendImage = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M49.9141 13.6797L44.4141 49.3438C44.3281 50.2031 43.8125 50.9766 43.0391 51.4062C42.6094 51.5781 42.1797 51.75 41.6641 51.75C41.3203 51.75 40.9766 51.6641 40.6328 51.4922L30.1484 47.1094L25.7656 53.6406C25.4219 54.2422 24.8203 54.5 24.2188 54.5C23.2734 54.5 22.5 53.7266 22.5 52.7812V44.5312C22.5 43.8438 22.6719 43.2422 23.0156 42.8125L41.75 18.75L16.4844 41.5234L7.63281 37.8281C6.6875 37.3984 6 36.5391 6 35.4219C5.91406 34.2188 6.42969 33.3594 7.375 32.8438L45.875 10.9297C46.7344 10.4141 47.9375 10.4141 48.7969 11.0156C49.6562 11.6172 50.0859 12.6484 49.9141 13.6797Z"
        fill={
          s360ConfigService.config?.sandBox
            ? s360ConfigService.config?.configDetails?.primaryColor
            : textValue === "" && attachemntFiles.length === 0
              ? "#758BA3"
              : s360ConfigService.config?.configDetails?.primaryColor ||
                "#005FEC"
        }
      />
    </svg>
  );
  const handleEmoji = (event, emojiObject) => {
    var preText = textValue;
    var emoji = emojiObject.emoji;
    // Sanitize the combined value before setting state
    const sanitizedValue = DOMPurify.sanitize(preText + emoji);
    setTextValue(sanitizedValue);
  };

  const [lastTimeCharTyped, setLastTimeCharTyped] = useState(-1);
  const setTyping = () => {
    setLastTimeCharTyped(new Date().getTime());
  };

  const typing = () => {
    if (lastTimeCharTyped === -1) {
      return;
    }

    const lvDeltaLastTimeTyping = new Date().getTime() - lastTimeCharTyped;
    if (lvDeltaLastTimeTyping > 4000) {
      // It has been at least 4 seconds without typing, stop the interval
      stopTimerTyping();
      return;
    }
    // Note: for supervisor users it won't hit API as per the angular service logic
    // workitemsService.chatService.typing(selectedTab, selectedTab?.data?.workitemId, {});
    if (!s360ConfigService?.config?.preview) {
      apiService.setTyping(props.currentWorkitemId);
    }
  };
  const timerTypingRef = useRef(null);
  const stopTimerTyping = () => {
    setLastTimeCharTyped(-1);

    if (timerTypingRef.current !== null) {
      clearInterval(timerTypingRef.current);
      timerTypingRef.current = null;
    }
  };

  useEffect(() => {
    if (lastTimeCharTyped !== -1) {
      if (timerTypingRef.current === null) {
        typing();
        timerTypingRef.current = setInterval(() => {
          typing();
        }, 1000);
      }
    }
  }, [lastTimeCharTyped]);

  function fileTypesToAcceptance() {
    let typesList = suppourtedFiles || ["*"];
    let acceptString = "";

    typesList.map((type, index) => {
      switch (type.toLowerCase()) {
        case "image/png":
          acceptString += ".png";
          break;
        case "image/jpeg":
          acceptString += ".jpg, .jpeg";
          break;
        case "application/pdf":
          acceptString += ".pdf";
          break;
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          acceptString += ".doc, .docx";
          break;
        case "text/plain":
          acceptString += ".txt";
          break;
        default:
          break;
      }
      if (index < typesList.length - 1) {
        acceptString += ", ";
      }
    });

    return acceptString;
  }

  const [typingAgentName, setTypingAgentName] = useState("");

  useEffect(() => {
    window.addEventListener("MessageTypingNotification", onAgentTyping);
    return () => {
      // Cleanup listener on unmount
      window.removeEventListener("MessageTypingNotification", onAgentTyping);
    };
  }, []);

  const onAgentTyping = ({ detail }) => {
    if (detail?.fromId) {
      setTypingAgentName(detail.fromId);
    } else {
      setTypingAgentName(null);
    }
  };
  const [showVoiceMic, setShowVoiceMic] = useState(false);
  const [audioRecordedFile, setAudioRecordedFile] = useState(null);
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);

  const handleVoiceMic = () => {
    setShowVoiceMic(!showVoiceMic);
  };

  const handleFilePickerOpen = () => {
    setIsFilePickerOpen(true);
  };

  const handleFilePickerClose = () => {
    setIsFilePickerOpen(false);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {showVoiceMic && (
        <VoiceCapture setAudioRecordedFile={setAudioRecordedFile} />
      )}

      <div>
        {typingAgentName && (
          <div
            style={{
              padding: "0 2rem",
              fontStyle: "italic",
            }}
          >
            {defaultBrandIcon()}
            <span>{typingAgentName} Typing...</span>
          </div>
        )}
        {
          <OutsideClickHandler
            onOutsideClick={() => {
              setShowEmoji(false);
            }}
          >
            {showEmoji && (
              <Picker
                disableSkinTonePicker
                onEmojiClick={handleEmoji}
                groupVisibility={{
                  flags: false,
                  animals_nature: false,
                  food_drink: false,
                  travel_places: false,
                  activities: false,
                  objects: false,
                  symbols: false,
                }}
                pickerStyle={{
                  position: "absolute",
                  top: "115px",
                  left: "9px",
                  width: "93%",
                  zIndex: "99999",
                }}
              />
            )}
            <div
              className={`s360-conversation-input-box-container${
                isFullScreen ? "-fs" : ""
              }`}
            >
              <div
                className={`s360-input-box-input-container${
                  isFullScreen ? "-fs" : ""
                }`}
                id="tx"
                style={{
                  ...containerStyle(),
                  maxWidth: !s360ConfigService?.getConfig().connectToNextiva
                    ? "75vw"
                    : "auto",
                }}
              >
                <div
                  style={{
                    maxWidth: `${
                      !s360ConfigService?.getConfig()?.connectToNextiva
                        ? "65vw"
                        : "75vw"
                    }`,
                  }}
                  className="input-box-bottom"
                >
                  {attachemntFiles.length > 0 && fileSize && (
                    <div className="file-info">
                      {/* <div className="file-info-cards">
                        <span>{fileSizeInMB}Mb</span>
                      </div>
                      {suppourtedFiles.map((type, index) => (
                                                 <div className="file-info-cards" key={index}>
                          <span>{type}</span>
                        </div>
                      ))} */}
                    </div>
                  )}
                  <TextareaAutosize
                    disabled={disableInput}
                    ref={ref}
                    id="tx1"
                    value={textValue}
                    className="s360-input-box-input"
                    placeholder={
                      attachemntFiles.length > 0
                        ? " "
                        : inputSpecs?.placeholderText
                          ? inputSpecs?.placeholderText
                          : defaultMessage
                    }
                    type="text"
                    onChange={onTextFieldChange}
                    onKeyDown={handleKeyPress}
                    // onFocus={() => setIsFocused(true)}
                    // onBlur={() => setIsFocused(false)}
                    // onKeyDown={(e: any) => { handleKeyDown(e) }}
                    onPaste={setTyping} // onTextFieldChange will handle sanitization after paste
                    style={{
                      backgroundColor: "white",
                      fontSize: "14px",
                      cursor: disableInput ? "not-allowed" : "default",
                    }}
                  />
                  <div className="s360-input-box-image-container" id="img2">
                    {!s360ConfigService?.config?.preview &&
                      attachemntFiles.length === 0 && (
                        <div
                          className={`s360-new-conversation-tab-image1${
                            isFullScreen ? "-fs" : ""
                          }`}
                          onClick={() =>
                            !isFilePickerOpen && setShowEmoji(!showEmoji)
                          }
                          style={{
                            opacity: isFilePickerOpen ? 0.5 : 1,
                            cursor: isFilePickerOpen
                              ? "not-allowed"
                              : "pointer",
                          }}
                        >
                          {emojiImage()}
                        </div>
                      )}

                    {s360ConfigService.config?.sandBox &&
                      !s360ConfigService.config?.hideAttachment && (
                        <div
                          className={`s360-new-conversation-tab-image1${
                            isFullScreen ? "-fs" : ""
                          }`}
                        >
                          {attachmentImage()}
                        </div>
                      )}

                    {s360ConfigService?.config?.connectToNextiva ? (
                      <div
                        className={`s360-new-conversation-tab-image1${
                          isFullScreen ? "-fs" : ""
                        }`}
                        onClick={onClickAttachment}
                      >
                        <input
                          style={{
                            opacity: "0",
                            width: "1px",
                            display: "none",
                          }}
                          ref={attachRef}
                          type="file"
                          multiple={true}
                          onChange={onChangeAttachment}
                          className="UploadFile image_uploader"
                          accept={fileTypesToAcceptance()}
                          placeholder={
                            inputSpecs?.placeholderText
                              ? inputSpecs?.placeholderText
                              : ""
                          }
                          onFocus={handleFilePickerOpen}
                          onBlur={handleFilePickerClose}
                        />

                        {attachmentImage()}
                      </div>
                    ) : (
                      !s360ConfigService?.config?.hideAttachment &&
                      (props.enableAttachment ||
                        flowMemorySingleton.getAgentMessageStarted()) && (
                        <div
                          className={`s360-new-conversation-tab-image1${
                            isFullScreen ? "-fs" : ""
                          }`}
                          onClick={
                            !s360ConfigService.config?.sandBox &&
                            onClickAttachment
                          }
                        >
                          <input
                            style={{
                              opacity: "0",
                              width: "1px",
                              display: "none",
                            }}
                            ref={attachRef}
                            type="file"
                            multiple={true}
                            onChange={onChangeAttachment}
                            accept={fileTypesToAcceptance()}
                            className="UploadFile image_uploader"
                            placeholder={
                              inputSpecs?.placeholderText
                                ? inputSpecs?.placeholderText
                                : ""
                            }
                            onFocus={handleFilePickerOpen}
                            onBlur={handleFilePickerClose}
                          />
                          {attachmentImage()}
                        </div>
                      )
                    )}
                    <div
                      className={`s360-new-conversation-tab-image1${
                        isFullScreen ? "-fs" : ""
                      }`}
                      onClick={
                        !s360ConfigService.config?.sandBox
                          ? handleClick
                          : undefined
                      }
                    >
                      {sendImage()}
                    </div>
                  </div>
                </div>
                {attachemntFiles.length > 0 && (
                  <FilePreview
                    files={attachemntFiles}
                    onRemovePreviewFile={props.onRemovePreviewFile}
                    CloseIconPreviewSvg={CloseIconPreviewSvg}
                  />
                )}

                {!s360ConfigService?.config?.preview &&
                  SpeechRecognition.browserSupportsSpeechRecognition() && (
                    <div
                      className="s360-input-box-micro"
                      ref={microphoneRef}
                      id="gif"
                      style={{
                        marginBottom: 0,
                        cursor: disableInput ? "not-allowed" : "default",
                        opacity: disableInput ? 0.2 : 1,
                      }}
                    >
                      <TextToSpeech
                        textValue={textValue}
                        setTextValue={(value) =>
                          setTextValue(DOMPurify.sanitize(value))
                        } // Sanitize voice input too
                        sent={sent}
                        {...props}
                        handleVoiceMic={handleVoiceMic}
                      />
                    </div>
                  )}
              </div>
            </div>
          </OutsideClickHandler>
        }
      </div>
    </div>
  );
});

export default InputBox;
